import { reactive, computed, readonly, nextTick, inject } from 'vue';
import { useSwiperNavigation } from './useSwiperNavigation';
import { useScrollSync } from './useScrollSync';
import { useFrameLayout } from './useFrameLayout';
import { useBusEvent } from '@rebuild/hooks/useBusEvent';

const defineTemplateConfig = {
	flow: {
		nodesMap: {
			menuItem: '.menu-item',
			moduleBlock: '.module-block',
			swiperContainer: '.flow-nav-swiper',
			swiperWrapper: '.swiper-core-wrapper',
			shadowItem: '.shadow-menu-word'
		},
		layoutOptions: {
			menuItemMargin: 40
		}
	},
	flowX: {
		nodesMap: {
			shadowItem: '.shadow-menu-bar-item',
			swiperWrapper: '.swiper-core-wrapper'
		},
		layoutOptions: {
			menuItemMargin: 80
		}
	}
};

/**
 * FlowNav 主状态管理器
 * 提供分层的 API 结构：actions(高级API) / modules(低级API) / state(状态访问) / utils(工具函数)
 */
export const useFlowNavCenter = config => {
	const { menus } = config;
	let nodesMap, layoutOptions;

	const templateType = inject('TEMPLATE_TYPE');
	const getTemplateType = computed(() => templateType?.value || '');
	const tmplConfig = defineTemplateConfig[getTemplateType.value];

	nodesMap = config.nodesMap || tmplConfig?.nodesMap || {};
	layoutOptions = config.layoutOptions || tmplConfig?.layoutOptions || {};

	// 中央状态管理
	const internalState = reactive({
		isInitialized: false,
		isDestroyed: false,
		currentMenus: menus || []
	});

	// 创建中央派发器，供子模块使用
	const centralDispatcher = {
		// 状态获取器
		getState: () => ({
			currentMenus: internalState.currentMenus,
			isDestroyed: internalState.isDestroyed
		}),
		// 配置获取器
		getConfig: () => ({
			nodesMap,
			layoutOptions
		}),
		// 回调注册器
		registerCallbacks: callbacks => {
			Object.assign(centralDispatcher, callbacks);
		}
	};

	// 各模块控制器的初始化，传入派发器
	const navigationController = useSwiperNavigation(centralDispatcher);
	const scrollSyncController = useScrollSync(centralDispatcher);
	const layoutController = useFrameLayout(centralDispatcher);

	// 注册动态数据获取器
	centralDispatcher.registerCallbacks({
		getComposeMenus: layoutController.getComposeMenus,
		stopScrollObserver: scrollSyncController.stopScrollObserver
	});

	// 高级 API - 业务操作层
	const actions = {
		// 切换到指定菜单（自动协调所有相关逻辑）
		switchToMenu: async menuId => {
			if (internalState.isDestroyed) return;
			if (!menuId) return;

			// 自动协调导航、滚动、布局
			navigationController.updateActiveMenuIdWithSwiper(menuId);
		},

		// 滚动到指定菜单（自动滚动并更新状态）
		scrollToMenu: menuItem => {
			if (internalState.isDestroyed) return;

			navigationController.switchMenu({
				menu: menuItem
			});
		},

		// 仅更新 Swiper 布局
		updateLayoutClear: async () => {
			if (internalState.isDestroyed) return;
			layoutController.calcSwiperMenu();
		},

		// 更新布局（自动更新布局和观察器）
		updateLayout: async () => {
			if (internalState.isDestroyed) return;

			const frameTasks = [
				() => layoutController.calcSwiperMenu(),
				() => scrollSyncController.createScrollObserver(actions.switchToMenu)
			];
			layoutController.updateFrame(frameTasks);
		},

		// 初始化所有功能
		initialize: async done => {
			if (internalState.isInitialized || internalState.isDestroyed) return;

			// 初始化活动菜单
			navigationController.initActiveMenu();
			// 挂载滚动联动功能
			scrollSyncController.mount(() => {
				nextTick(() => {
					actions.updateLayout();
				});
			});

			internalState.isInitialized = true;
			done?.();
		},

		// 销毁所有资源
		destroy: () => {
			if (internalState.isDestroyed) return;

			// 卸载滚动联动功能
			scrollSyncController.unmount();

			internalState.isDestroyed = true;
		}
	};

	// 低级 API - 模块控制层
	const modules = {
		navigation: {
			updateActiveMenuId: navigationController.updateActiveMenuId,
			switchMenu: navigationController.switchMenu,
			initActiveMenu: navigationController.initActiveMenu,
			updateActiveMenuIdWithSwiper: navigationController.updateActiveMenuIdWithSwiper
		},
		scrollSync: {
			createScrollObserver: scrollSyncController.createScrollObserver,
			stopScrollObserver: scrollSyncController.stopScrollObserver,
			mount: scrollSyncController.mount,
			unmount: scrollSyncController.unmount
		},
		layout: {
			calcSwiperMenu: layoutController.calcSwiperMenu,
			updateFrame: layoutController.updateFrame
		}
	};

	// 关联外部调用
	const refs = {
		swiperInstance: navigationController.swiperInstance
	};

	// 状态访问层 - 只读
	const state = {
		// 导航状态
		activeMenuId: computed(() => navigationController.activeMenuId.value),
		// 布局状态
		composeMenus: computed(() => layoutController.composeMenus.value),
		// 内部状态
		isInitialized: computed(() => internalState.isInitialized),
		isDestroyed: computed(() => internalState.isDestroyed),
		currentMenus: computed(() => internalState.currentMenus)
	};

	// 工具层 - 辅助功能
	const utils = {
		// 调试状态信息
		debugState: () => {
			return {
				activeMenuId: state.activeMenuId.value,
				composeMenus: state.composeMenus.value,
				isInitialized: state.isInitialized.value,
				isDestroyed: state.isDestroyed.value
			};
		}
	};

	// -------
	useBusEvent('iframe-message', msgData => {
		const { activeModuleId } = msgData;
		if (getTemplateType.value === 'flow' && activeModuleId) {
			modules.navigation.switchMenu({
				module: { moduleId: activeModuleId }
			});
		}
	});
	// -------

	return {
		actions,
		modules,
		refs,
		state: readonly(state),
		utils
	};
};
